import { useCallback } from "react";

import { useFormikContext } from "formik";
import { Button } from "reactstrap";
import type { RenderPlaceholderProps } from "slate-react";

import {
  RichTextEditor,
  type Descendant,
} from "components/RichTextEditor/RichTextEditor";
import { useConfirmLoseUnsaved } from "hooks/useConfirmLoseUnsaved";

import type { FormValues } from "./types";

interface LandingPageEditorFormFieldsProps {
  hasUnsavedChanges: boolean;
  isOpen: boolean;
}

export const LandingPageEditorFormFields = ({
  hasUnsavedChanges,
  isOpen,
}: LandingPageEditorFormFieldsProps) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  useConfirmLoseUnsaved({
    when: hasUnsavedChanges && isOpen,
    message:
      "You have unsaved changes to the landing page content. Are you sure you want to leave without saving?",
  });

  const handleDebugClick = useCallback(() => {
    const debugData = {
      overview: values.overview,
      brief: values.brief,
      faq: values.faq,
      overviewJson: JSON.stringify(values.overview, null, 2),
      briefJson: JSON.stringify(values.brief, null, 2),
      faqJson: JSON.stringify(values.faq, null, 2),
    };
    alert(`RTE Content logged to console. Check developer tools.`);
  }, [values.overview, values.brief, values.faq]);

  const handlePreviewToggle = useCallback(() => {
    setFieldValue("isPreview", !values.isPreview);
  }, [setFieldValue, values.isPreview]);

  const handleOverviewChange = useCallback(
    (newValue: Descendant[]) => setFieldValue("overview", newValue),
    [setFieldValue],
  );

  const handleBriefChange = useCallback(
    (newValue: Descendant[]) => setFieldValue("brief", newValue),
    [setFieldValue],
  );

  const handleFaqChange = useCallback(
    (newValue: Descendant[]) => setFieldValue("faq", newValue),
    [setFieldValue],
  );

  const renderOverviewPlaceholder = useCallback(
    ({ attributes, children }: RenderPlaceholderProps) => (
      <div {...attributes} style={{ color: "#6c757d", fontStyle: "italic" }}>
        <p>{children}</p>
        <p>
          Suggested topics: the campaign&lsquo;s main focus, key messaging,
          target audience, audience benefits, unique features, or other standout
          highlights...
        </p>
      </div>
    ),
    [],
  );

  return (
    <div
      className="d-flex flex-column"
      style={{ height: "80vh", overflow: "auto" }}
    >
      {import.meta.env.DEV && (
        <div className="d-flex justify-content-end mb-3">
          <Button
            color="info"
            outline
            size="sm"
            className="me-2"
            title="Log RTE content structure to browser console for debugging"
            onClick={handleDebugClick}
          >
            Debug JSON
          </Button>
          <Button color="secondary" outline onClick={handlePreviewToggle}>
            {values.isPreview ? "Edit" : "Preview"}
          </Button>
        </div>
      )}
      <div className="landing-page-editor d-flex flex-column gap-5 pb-5 h-100">
        <div className="mb-5" style={{ height: "20vh", minHeight: "12rem" }}>
          <h3 className="mb-3">Campaign Overview</h3>
          <div className="d-flex flex-column border rounded h-100">
            <RichTextEditor
              value={values.overview}
              onChange={handleOverviewChange}
              readOnly={values.isPreview}
              renderPlaceholder={renderOverviewPlaceholder}
              placeholder="Enter a concise, high-level summary of the campaign (about 2–3 paragraphs)..."
            />
          </div>
        </div>
        <div
          className="d-flex flex-column mb-5"
          style={{ height: "60vh", minHeight: "15rem" }}
        >
          <h3 className="mb-3">Campaign Brief</h3>
          <div className="d-flex flex-column border rounded h-100">
            <RichTextEditor
              value={values.brief}
              onChange={handleBriefChange}
              readOnly={values.isPreview}
              placeholder="Enter campaign brief content..."
            />
          </div>
        </div>
        <div
          className="d-flex flex-column"
          style={{ height: "60vh", minHeight: "15rem" }}
        >
          <h3 className="mb-3">Frequently Asked Questions</h3>
          <div className="d-flex flex-column border rounded h-100">
            <RichTextEditor
              value={values.faq}
              onChange={handleFaqChange}
              readOnly={values.isPreview}
              placeholder="Enter frequently asked questions..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};
